/**
 * Modo "Profissionais" da agenda: de quem é cada agendamento.
 * Usado pelo Calendar (computador) e pelo CalendarMobile (celular).
 *
 * A API manda o profissional vazio como null, '' ou "null"; -1 é o bloqueio geral.
 */
import AppointmentsState from '../../../consts/AppointmentsState';
import { intervalsForWeekday } from '../../../utils/resourceSchedule';

export const UNASSIGNED_COLUMN_ID = '__unassigned__';

// Com poucas agendas tudo cabe na tela e a ordem fixa é mais fácil de achar;
// só acima disto as agendas sem nada no dia vão para o fim da fila
export const IDLE_TO_END_MIN_COLUMNS = 6;

// Agendamento legado sem profissional (vai para a coluna "Sem profissional")
function isUnassigned(apt) {
  return apt.resource == null || apt.resource === '' || apt.resource === 'null';
}

// -1 é o bloqueio geral (vale para todas as agendas); nunca é "sem profissional".
// Um bloqueio geral cancelado simplesmente não aparece em coluna nenhuma.
function hasNoResource(apt) {
  return isUnassigned(apt) || String(apt.resource) === '-1';
}

// Bloqueio geral (e o fantasma do drawer sem agenda escolhida) vale para todas as colunas
function belongsToEveryColumn(apt) {
  return (apt.id === '__ghost__' || apt.status === AppointmentsState.Blocked) && hasNoResource(apt);
}

export function belongsToColumn(apt, columnId) {
  if (belongsToEveryColumn(apt)) return true;
  if (columnId === UNASSIGNED_COLUMN_ID) return isUnassigned(apt);
  return !hasNoResource(apt) && String(apt.resource) === String(columnId);
}

// A coluna tem algo "dela" no dia (agendamento ou bloqueio específico)? O bloqueio
// geral e o fantasma do drawer valem para todas e não contam: é o que decide se a
// coluna do profissional fica recolhida no modo "Profissionais".
export function columnHasOwnItems(dayAppointments, columnId) {
  return dayAppointments.some((apt) => (
    apt.status !== AppointmentsState.Available
    && !belongsToEveryColumn(apt)
    && belongsToColumn(apt, columnId)
  ));
}

// Partículas que não servem de sobrenome ("Pereira da Silva" -> "Silva")
const NAME_PARTICLES = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);
const SHORT_LABEL_MAX = 18;
// Cabeçalho da coluna estreita: o que cabe em 100px com 13px semibold ("Jessica B.")
export const NARROW_LABEL_MAX = 11;

// Largura das colunas do modo "Profissionais" (espelha .dayColResource e
// .dayColResourceNarrow em Calendar.module.css); as setas do teclado rolam um passo
export const WIDE_COLUMN_WIDTH_PX = 160;
export const NARROW_COLUMN_WIDTH_PX = 100;

// Nome curto para o cabeçalho da coluna: nome completo quando cabe; senão primeiro
// nome + último sobrenome ("Neile Silva"); se ainda passar, só a inicial ("Neile S.").
// `max` encolhe o limite na coluna estreita.
export function shortResourceLabel(label, max = SHORT_LABEL_MAX) {
  const full = String(label || '').trim();
  if (full.length <= max) return full;
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return full;
  let lastIndex = parts.length - 1;
  while (lastIndex > 0 && NAME_PARTICLES.has(parts[lastIndex].toLowerCase())) lastIndex -= 1;
  if (lastIndex === 0) return full;
  const first = parts[0];
  const last = parts[lastIndex];
  const withLast = `${first} ${last}`;
  if (withLast.length <= max) return withLast;
  return `${first} ${last[0].toUpperCase()}.`;
}

// Agendamento sem profissional (legado) ganha coluna própria, só se existir no dia
export function buildResourceColumns(resourceColumns, dayAppointments) {
  const hasUnassigned = dayAppointments.some((apt) => !belongsToEveryColumn(apt) && isUnassigned(apt));
  return hasUnassigned
    ? [...resourceColumns, { id: UNASSIGNED_COLUMN_ID, label: 'Sem profissional' }]
    : resourceColumns;
}

// Id que vai para o novo agendamento ao clicar na coluna (a coluna "Sem profissional" não pré-preenche)
export function columnResourceId(column) {
  return column.id === UNASSIGNED_COLUMN_ID ? null : column.id;
}

/**
 * Props de dia da coluna (Calendar e CalendarMobile). Com horário próprio a
 * coluna segue as faixas do profissional; sem, segue a empresa (dias, horários
 * bloqueados e, quando a grade foi esticada por outra agenda, listras fora do
 * horário da empresa via companyHours [início, fim] em horas).
 */
export function columnDayProps({ resourceSchedules, availableDaysOfWeek, blockedTimes, companyHours }, id, day) {
  const ownHours = id == null ? null : intervalsForWeekday(resourceSchedules?.[String(id)], day.getDay());
  if (ownHours) return { ownHours, isNonWorkingDay: ownHours.length === 0, blockedHours: [] };
  return {
    ownHours: companyHours ? [[Math.round(companyHours[0] * 60), Math.round(companyHours[1] * 60)]] : null,
    isNonWorkingDay: availableDaysOfWeek ? !availableDaysOfWeek.includes(day.getDay()) : false,
    blockedHours: blockedTimes,
  };
}
